import { Box, Button, Pagination, Skeleton, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { fetchSessions, inactiveSession } from "../SettingsService";
import {
  convertESTtoUserLocalTime,
  handleErrorMessage,
  openSuccessNotification,
} from "helpers/methods";
import { useAppSelector } from "hooks/use-app-select";
import { selectAccessToken, selectId } from "redux/authSlice";
import securityStyles from "./Security.styles";
import securityTokens from "./Security.tokens";
import { SecurityDeviceKind, parseSessionUserAgent } from "./SecurityHelper";
import {
  DeviceDesktopIcon,
  DeviceMobileIcon,
  DeviceTabletIcon,
} from "./components/SecurityIcons";

const styles = securityStyles;
const { table } = securityTokens;

/** The design shows a plain list; the pager only appears once it overflows. */
const ROWS_PER_PAGE = 5;

interface Session {
  account: string;
  app: string;
  email: string;
  accessToken: string;
  refreshToken: string;
  userAgent: string;
  lastLogin?: string;
  status: string;
  ip: string;
}

const DEVICE_ICONS: Record<SecurityDeviceKind, typeof DeviceDesktopIcon> = {
  desktop: DeviceDesktopIcon,
  mobile: DeviceMobileIcon,
  tablet: DeviceTabletIcon,
};

const Sessions = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);

  const loggedInUserAuthToken: string = useAppSelector(selectAccessToken);
  const loggedInUserId: string = useAppSelector(selectId);

  useEffect(() => {
    getSessions();
  }, []);

  const getSessions = async () => {
    try {
      setLoading(true);
      const response = await fetchSessions();
      setSessions(response ?? []);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  const isCurrentDevice = (selectedAccessToken: string) =>
    selectedAccessToken === loggedInUserAuthToken;

  const handleLogout = async (accessToken: string) => {
    try {
      setLoading(true);
      await inactiveSession(loggedInUserId, accessToken);
      openSuccessNotification("Logged out successfully.");
      await getSessions();
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const pageCount = Math.ceil(sessions.length / ROWS_PER_PAGE);
  const currentPage = Math.min(page, Math.max(pageCount, 1));
  const visibleSessions = sessions.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE,
  );

  const getSessionRow = (session: Session, index: number) => {
    const { label, kind } = parseSessionUserAgent(session.userAgent);
    const DeviceIcon = DEVICE_ICONS[kind];
    const isCurrent = isCurrentDevice(session.accessToken);

    return (
      <Box
        key={`${session.accessToken}-${index}`}
        sx={styles.tableRow(
          table.sessionColumns,
          index === visibleSessions.length - 1,
        )}
      >
        <Box sx={styles.deviceCell}>
          <DeviceIcon
            sx={styles.deviceIcon}
            color={table.deviceIconColor}
            strokeWidth={table.deviceIconStrokeWidth}
          />
          <Box component="span" sx={styles.deviceName}>
            {label}
            {isCurrent && (
              <Box component="span" sx={styles.rowBadge}>
                THIS DEVICE
              </Box>
            )}
          </Box>
        </Box>
        <Box component="span" sx={styles.mutedCell}>
          {session.lastLogin
            ? convertESTtoUserLocalTime(
                session.lastLogin,
                "YYYY-MM-DD HH:mm:ss",
                "MMMM D, YYYY h:mm A",
              )
            : "-"}
        </Box>
        <Box component="span" sx={styles.mutedCell}>
          {session.ip}
        </Box>
        <Box sx={styles.actionCell}>
          <Button
            disableRipple
            aria-label={`Sign out ${label}`}
            disabled={loading}
            onClick={() => handleLogout(session.accessToken)}
            sx={styles.rowActionLink}
          >
            Sign out
          </Button>
        </Box>
      </Box>
    );
  };

  const getSkeletonRows = () =>
    [...Array(table.skeletonRows)].map((_, index) => (
      <Box
        key={index}
        sx={styles.tableRow(
          table.sessionColumns,
          index === table.skeletonRows - 1,
        )}
      >
        <Skeleton variant="text" width="55%" sx={styles.tableSkeletonCell} />
        <Skeleton variant="text" width="70%" sx={styles.tableSkeletonCell} />
        <Skeleton variant="text" width="60%" sx={styles.tableSkeletonCell} />
        <Box sx={styles.actionCell}>
          <Skeleton variant="text" width={48} sx={styles.tableSkeletonCell} />
        </Box>
      </Box>
    ));

  return (
    <Box sx={styles.splitCard}>
      <Box sx={styles.splitCardHeader}>
        <Box sx={styles.splitCardHeaderText}>
          <Typography component="h2" sx={styles.cardTitle}>
            Device &amp; Login Activity
          </Typography>
          <Typography component="div" sx={styles.cardDescription}>
            Devices currently signed in to your account.
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.tableHeader(table.sessionColumns)}>
        <Box component="span">DEVICE</Box>
        <Box component="span">LAST LOGIN</Box>
        <Box component="span">IP ADDRESS</Box>
        <Box component="span" sx={styles.tableHeaderActionCell}>
          ACTION
        </Box>
      </Box>

      {loading ? (
        getSkeletonRows()
      ) : visibleSessions.length > 0 ? (
        visibleSessions.map(getSessionRow)
      ) : (
        <Box sx={styles.tableEmptyState}>No active sessions.</Box>
      )}

      {!loading && pageCount > 1 && (
        <Box sx={styles.paginationBar}>
          <Pagination
            page={currentPage}
            count={pageCount}
            siblingCount={1}
            boundaryCount={1}
            shape="rounded"
            onChange={(_event, newPage: number) => setPage(newPage)}
            sx={styles.pagination}
          />
        </Box>
      )}
    </Box>
  );
};

export default Sessions;
